/* Clone Lab — Stack
   Eigen gedrag, geen externe bibliotheken.
   In het React-thema stond dit op useState; hier is het gedelegeerde DOM-code
   die ook werkt wanneer de themabewerker een sectie opnieuw inlaadt. */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Carrousel ------------------------------------------------------ */

  function Carousel(root) {
    var rail = root.querySelector('[data-carousel-rail]');
    if (!rail) return;

    var buttons = root.querySelectorAll('[data-carousel-prev], [data-carousel-next]');

    function step() {
      var card = rail.firstElementChild;
      var width = card ? card.offsetWidth : 328;
      var gap = parseFloat(getComputedStyle(rail).columnGap || '16') || 16;
      return width + gap;
    }

    function update() {
      var max = rail.scrollWidth - rail.clientWidth - 1;
      root.querySelectorAll('[data-carousel-prev]').forEach(function (b) {
        b.disabled = rail.scrollLeft <= 1;
      });
      root.querySelectorAll('[data-carousel-next]').forEach(function (b) {
        b.disabled = rail.scrollLeft >= max;
      });
    }

    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        var direction = button.hasAttribute('data-carousel-prev') ? -1 : 1;
        rail.scrollBy({
          left: direction * step(),
          behavior: REDUCED ? 'auto' : 'smooth'
        });
      });
    });

    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    return {
      destroy: function () { window.removeEventListener('resize', update); }
    };
  }

  /* ---- Filterpillen --------------------------------------------------- */
  /* Volwaardig ARIA-tabs-patroon, passend bij de rollen die de sectie al
     aankondigt: de pillen zijn tabs (aria-selected + aria-controls), de
     panelen tabpanels, en hier komt het gedrag bij dat daarbij hoort —
     roving tabindex (alleen de actieve pil zit in de tabvolgorde) en
     pijltjestoetsen links/rechts met Home/End, waarbij de focus meeverhuist
     en het paneel direct meewisselt (automatische activering: de panelen
     staan al in de pagina, wisselen kost niets). Alle listeners zitten op
     elementen binnen de sectie zelf, dus er valt niets op te ruimen bij
     shopify:section:unload. */

  function Filters(root) {
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-filter-pill]'));
    var panels = root.querySelectorAll('[data-filter-panel]');
    if (!pills.length) return;

    function activate(pill, moveFocus) {
      var target = pill.getAttribute('data-filter-pill');
      pills.forEach(function (p) {
        var selected = p === pill;
        p.setAttribute('aria-selected', String(selected));
        p.setAttribute('tabindex', selected ? '0' : '-1');
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-filter-panel') !== target;
      });
      root.querySelectorAll('[data-carousel-rail]').forEach(function (rail) {
        rail.scrollLeft = 0;
      });
      if (moveFocus) pill.focus();
    }

    pills.forEach(function (pill, index) {
      pill.addEventListener('click', function () {
        activate(pill, false);
      });

      pill.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = (index + 1) % pills.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + pills.length) % pills.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = pills.length - 1;
        if (next === null) return;
        event.preventDefault();
        activate(pills[next], true);
      });
    });
  }

  /* ---- Aankondigingsbalk: rotatie en sluitknop ------------------------ */
  /* Contract met sections/announcement-bar.liquid: wrapper [data-announcement]
     met data-rotate in seconden, berichten [data-announcement-item] (het
     eerste zichtbaar, de rest `hidden`), sluitknop [data-announcement-close].
     Bij voorkeur voor verminderde beweging roteren we niet en blijft alleen
     het eerste bericht staan. Het sluiten onthouden we per sessie; een
     geblokkeerde sessionStorage mag verder niets breken. In de themabewerker
     blijft de balk altijd staan, anders raakt de winkelier hem kwijt. */

  function Announcement(root) {
    var items = root.querySelectorAll('[data-announcement-item]');
    var closeButton = root.querySelector('[data-announcement-close]');
    var KEY = 'theme:announcement-dismissed';
    var designMode = window.Shopify && window.Shopify.designMode;

    if (!designMode) {
      try {
        if (window.sessionStorage && window.sessionStorage.getItem(KEY) === '1') {
          root.hidden = true;
          return;
        }
      } catch (error) { /* geblokkeerde storage: balk gewoon tonen */ }
    }

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        root.hidden = true;
        try {
          if (window.sessionStorage) window.sessionStorage.setItem(KEY, '1');
        } catch (error) { /* niet erg: dan komt hij volgende pagina terug */ }
      });
    }

    if (items.length < 2 || REDUCED) return;

    var interval = (parseInt(root.getAttribute('data-rotate'), 10) || 5) * 1000;
    var index = 0;
    var paused = false;

    function show(next) {
      items.forEach(function (item, i) { item.hidden = i !== next; });
      index = next;
    }

    var timer = window.setInterval(function () {
      if (paused || root.hidden) return;
      show((index + 1) % items.length);
    }, interval);

    root.addEventListener('mouseenter', function () { paused = true; });
    root.addEventListener('mouseleave', function () { paused = false; });
    root.addEventListener('focusin', function () { paused = true; });
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) paused = false;
    });

    return {
      destroy: function () { window.clearInterval(timer); }
    };
  }

  /* ---- Header: mega-menu, zoeken, winkelwagen ------------------------- */

  function Header(root) {
    var openPanel = null;
    var closeTimer = null;

    function close() {
      if (!openPanel) return;
      openPanel.panel.hidden = true;
      openPanel.trigger.setAttribute('aria-expanded', 'false');
      openPanel = null;
    }

    function open(trigger, panel) {
      if (openPanel && openPanel.panel === panel) return;
      close();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      openPanel = { trigger: trigger, panel: panel };
    }

    root.querySelectorAll('[data-menu-trigger]').forEach(function (trigger) {
      var id = trigger.getAttribute('data-menu-trigger');
      var panel = root.querySelector('[data-menu-panel="' + id + '"]');
      if (!panel) return;

      var item = trigger.closest('li') || trigger;

      function show() {
        window.clearTimeout(closeTimer);
        open(trigger, panel);
      }
      function hide() {
        closeTimer = window.setTimeout(close, 120);
      }

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (openPanel && openPanel.panel === panel) close();
        else open(trigger, panel);
      });
      item.addEventListener('mouseenter', show);
      item.addEventListener('mouseleave', hide);
      panel.addEventListener('mouseenter', show);
      panel.addEventListener('mouseleave', hide);
      trigger.addEventListener('focus', show);
    });

    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) close();
    });

    /* Zoeken
       Het icoon in de kop is en blijft een link naar de zoekpagina, zodat er
       zonder JavaScript nog steeds gezocht kan worden. Hier maken we er een
       schakelaar van die het veld onder de balk openklapt. De focus springt het
       veld in en bij sluiten terug naar het icoon dat het opende; Escape, de
       sluitknop en een klik ernaast sluiten allemaal. */

    /* Winkelwagen
       Het icoon blijft een link naar /cart; hier wordt het pas een schakelaar
       voor het paneel, net als het zoekicoon hieronder. Staat het paneel er
       niet — bijvoorbeeld op de winkelwagenpagina zelf — dan laten we de link
       gewoon zijn werk doen. */

    root.querySelectorAll('[data-cart-drawer-open]').forEach(function (opener) {
      opener.addEventListener('click', function (event) {
        if (!cartDrawer) return;
        event.preventDefault();
        if (cartDrawer.isOpen()) cartDrawer.close();
        else cartDrawer.open(opener);
      });
    });

    // De zoekla is een eigen sectie in de header-groep en staat dus buiten
    // dit element; daarom document-breed zoeken en niet binnen `root`.
    var searchPanel = document.querySelector('[data-search-panel]');
    var searchToggles = root.querySelectorAll('[data-search-toggle]');
    var searchOpener = null;

    function setSearch(state) {
      if (!searchPanel) return;
      if (state === !searchPanel.hidden) return;
      searchPanel.hidden = !state;
      searchToggles.forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', String(state));
      });
      if (state) {
        close();
        var input = searchPanel.querySelector('[data-search-input]');
        if (input) input.focus();
      } else if (searchOpener) {
        searchOpener.focus();
        searchOpener = null;
      }
    }

    searchToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        if (!searchPanel) return;
        event.preventDefault();
        var opening = searchPanel.hidden;
        searchOpener = opening ? toggle : null;
        setSearch(opening);
      });
    });

    root.querySelectorAll('[data-search-close]').forEach(function (b) {
      b.addEventListener('click', function () { setSearch(false); });
    });

    /* --- Zoekla met meetypende resultaten -------------------------------
       Het paneel is met CSS een la geworden die van rechts inschuift, in
       hetzelfde formaat als het winkelwagenpaneel. Twee dingen worden hier
       toegevoegd die niet in de opmaak van de sectie staan, zodat de kop
       ongewijzigd blijft: een donkere laag eronder om op te klikken, en een
       vak waarin de resultaten landen.

       De resultaten komen van Shopify's eigen suggest-endpoint, opgehaald
       als sectie en niet als JSON. Daardoor rekent Liquid de prijzen uit in
       de winkelvaluta en hoeft dat hier niet nagebouwd te worden.

       Tussen twee toetsaanslagen zit 250ms wachttijd. Zonder die pauze stuur
       je bij een woord van acht letters acht verzoeken, terwijl alleen het
       laatste ertoe doet. Een lopend verzoek wordt afgebroken zodra er een
       nieuwe aanvraag komt, anders kan een traag antwoord van "sh" over een
       vers antwoord van "shell" heen vallen. */
    if (searchPanel) {
      var searchForm = searchPanel.querySelector('form');
      var searchInput = searchPanel.querySelector('[data-search-input]');

      var searchOverlay = document.createElement('div');
      searchOverlay.className = 'header__search-overlay';
      searchOverlay.setAttribute('data-search-overlay', '');
      searchOverlay.hidden = true;
      searchOverlay.addEventListener('click', function () { setSearch(false); });
      searchPanel.parentNode.insertBefore(searchOverlay, searchPanel);

      var searchResults = document.createElement('div');
      searchResults.className = 'search-results';
      searchResults.setAttribute('data-search-results', '');
      searchResults.setAttribute('aria-live', 'polite');
      if (searchForm) {
        searchForm.parentNode.insertBefore(searchResults, searchForm.nextSibling);
      }

      var searchTimer = null;
      var searchController = null;
      var searchPromos = searchPanel.querySelector('[data-search-promos]');

      // De tegels en de resultaten delen dezelfde ruimte: is er iets getypt,
      // dan wijken de tegels; is het veld leeg, dan staan ze er weer.
      function showPromos(show) {
        if (searchPromos) searchPromos.hidden = !show;
      }

      function renderResults(html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector('[data-search-results-inner]');
        searchResults.innerHTML = inner ? inner.innerHTML : '';
      }

      function runSearch(term) {
        if (searchController) searchController.abort();
        searchController = new AbortController();
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        // Welke soorten treffers en hoeveel per soort komt uit de
        // instellingen van de kop, meegegeven als data-attribuut. Zo bepaalt
        // de winkelier het in de customizer in plaats van hier in de code.
        var types = searchPanel.getAttribute('data-search-types') || 'product';
        var limit = searchPanel.getAttribute('data-search-limit') || '6';
        var url = root + 'search/suggest?q=' + encodeURIComponent(term) +
          '&section_id=predictive-search' +
          '&resources[type]=' + encodeURIComponent(types) +
          '&resources[limit]=' + encodeURIComponent(limit);
        searchPanel.classList.add('is-loading');
        fetch(url, { signal: searchController.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            searchPanel.classList.remove('is-loading');
            renderResults(html);
          })
          .catch(function () { /* afgebroken of mislukt: laat staan wat er stond */ });
      }

      if (searchInput) {
        searchInput.addEventListener('input', function () {
          var term = searchInput.value.trim();
          if (searchTimer) clearTimeout(searchTimer);
          if (term.length < 2) {
            if (searchController) searchController.abort();
            searchPanel.classList.remove('is-loading');
            searchResults.innerHTML = '';
            showPromos(true);
            return;
          }
          showPromos(false);
          searchTimer = setTimeout(function () { runSearch(term); }, 250);
        });
      }

      var baseSetSearch = setSearch;
      setSearch = function (state) {
        baseSetSearch(state);
        searchOverlay.hidden = !state;
        document.documentElement.classList.toggle('is-search-open', state);
        if (!state) {
          if (searchTimer) clearTimeout(searchTimer);
          if (searchController) searchController.abort();
          searchPanel.classList.remove('is-loading');
          searchResults.innerHTML = '';
          showPromos(true);
          if (searchInput) searchInput.value = '';
        }
      };
    }

    function onDocumentClick(event) {
      if (!searchPanel || searchPanel.hidden) return;
      if (root.contains(event.target)) return;
      setSearch(false);
    }

    function onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      close();
      setSearch(false);
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);

    return {
      destroy: function () {
        window.clearTimeout(closeTimer);
        document.removeEventListener('click', onDocumentClick);
        document.removeEventListener('keydown', onDocumentKeydown);
      }
    };
  }

  /* ---- Collectie: filterpaneel ---------------------------------------- */
  /* Het paneel is een <details> met een gewoon GET-formulier: zonder deze code
     klapt het gewoon open en filtert de verzendknop. Wat hier bij komt is
     comfort — zelf verzenden bij een wijziging, en sluiten bij Escape of een
     klik ernaast. */

  function CollectionFilters(details) {
    var form = details.querySelector('[data-filter-form]');
    if (!form) return;

    var timer = null;

    function submit() {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      }, 250);
    }

    form.addEventListener('change', function (event) {
      if (event.target.type === 'number') return;
      submit();
    });

    form.addEventListener('input', function (event) {
      if (event.target.type !== 'number') return;
      submit();
    });

    function onDocumentClick(event) {
      if (!details.open) return;
      if (details.contains(event.target)) return;
      details.open = false;
    }

    document.addEventListener('click', onDocumentClick);

    details.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      details.open = false;
      var summary = details.querySelector('summary');
      if (summary) summary.focus();
    });

    return {
      destroy: function () {
        window.clearTimeout(timer);
        document.removeEventListener('click', onDocumentClick);
      }
    };
  }

  /* ---- Collectie: SEO-tekst inkorten ---------------------------------- */
  /* Zonder JavaScript staat de volledige tekst er; hier korten we hem in en
     tonen we de knop alleen als er echt meer staat dan past. */

  function ReadMore(root) {
    var text = root.querySelector('[data-read-more-text]');
    var toggle = root.querySelector('[data-read-more-toggle]');
    var label = root.querySelector('[data-read-more-label]');
    if (!text || !toggle) return;

    var expanded = false;

    function measure() {
      if (expanded) return;
      root.classList.add('is-clamped');
      var overflows = text.scrollHeight - text.clientHeight > 1;
      toggle.hidden = !overflows;
      if (!overflows) root.classList.remove('is-clamped');
    }

    toggle.addEventListener('click', function () {
      expanded = !expanded;
      root.classList.toggle('is-clamped', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      if (label) {
        label.textContent = expanded
          ? toggle.getAttribute('data-label-less')
          : toggle.getAttribute('data-label-more');
      }
    });

    window.addEventListener('resize', measure);
    measure();

    return {
      destroy: function () { window.removeEventListener('resize', measure); }
    };
  }

  /* ---- Productpagina --------------------------------------------------- */

  /* Galerij, varianten, aantal en de balk die bij scrollen verschijnt. Alles
     leest uit het JSON-blok dat de sectie meegeeft; er staat geen prijs en geen
     variant in deze code. */
  function ProductMain(root) {
    var variants = [];
    var dataEl = root.querySelector('[data-variant-json]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent) || []; } catch (error) { variants = []; }
    }

    /* -- Galerij -- */
    var stage = root.querySelector('[data-gallery-stage]');
    var slides = root.querySelectorAll('[data-gallery-slide]');
    var thumbs = root.querySelectorAll('[data-gallery-thumb]');
    /* De puntjes onder het beeld zijn dezelfde navigatie als de duimnagels,
       alleen een andere weergave. Ze krijgen daarom een eigen attribuut in
       plaats van hetzelfde: staan beide sets in de pagina (duimnagels op
       desktop, puntjes op mobiel), dan zou één lijst de tellingen door elkaar
       gooien en zou puntje 1 naar beeld 5 springen. */
    var dots = root.querySelectorAll('[data-gallery-dot]');

    function markThumb(index) {
      thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', String(i === index));
      });
      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', String(i === index));
      });
    }

    function goToSlide(index) {
      if (!stage || !slides[index] || !slides[0]) return;
      stage.scrollTo({
        left: slides[index].offsetLeft - slides[0].offsetLeft,
        behavior: REDUCED ? 'auto' : 'smooth'
      });
      markThumb(index);
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () { goToSlide(index); });
    });
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () { goToSlide(index); });
    });

    /* -- Pijlknoppen naast het beeld -- */
    /* Zelfde gedrag als de carrouselknoppen: één beeld per klik via
       goToSlide(), en aan de uiteinden gaat de knop op disabled. De sectie
       rendert de knoppen alleen bij meer dan één beeld. */
    var navPrev = root.querySelector('[data-gallery-prev]');
    var navNext = root.querySelector('[data-gallery-next]');

    function currentSlide() {
      if (!stage) return 0;
      var width = stage.clientWidth || 1;
      return Math.min(Math.round(stage.scrollLeft / width), slides.length - 1);
    }

    function updateNav() {
      if (!navPrev && !navNext) return;
      var index = currentSlide();
      if (navPrev) navPrev.disabled = index <= 0;
      if (navNext) navNext.disabled = index >= slides.length - 1;
    }

    if (navPrev) {
      navPrev.addEventListener('click', function () {
        goToSlide(Math.max(0, currentSlide() - 1));
      });
    }
    if (navNext) {
      navNext.addEventListener('click', function () {
        goToSlide(Math.min(slides.length - 1, currentSlide() + 1));
      });
    }

    if (stage) {
      stage.addEventListener('scroll', function () {
        markThumb(currentSlide());
        updateNav();
      }, { passive: true });
    }
    updateNav();

    /* -- Zoom (lightbox) -- */
    /* Zelfde dialoogpatroon als het winkelwagenpaneel en de mobiele
       navigatie: focus het paneel in, focusval met Tab, Escape en de
       sluitknop sluiten, klik op de overlay of naast het beeld sluit ook, en
       de focus keert terug naar het beeld dat de zoom opende. Het element
       bestaat alleen als de sectie-instelling enable_zoom aan staat; de
       pijltjestoetsen bladeren binnen de lightbox. */
    var lightbox = root.querySelector('[data-lightbox]');
    var lightboxApi = null;

    if (lightbox) {
      lightboxApi = (function () {
        var panel = lightbox.querySelector('[data-lightbox-panel]');
        var rail = lightbox.querySelector('[data-lightbox-rail]');
        var lbSlides = lightbox.querySelectorAll('[data-lightbox-slide]');
        if (!panel || !rail || !lbSlides.length) return null;

        var lbPrev = lightbox.querySelector('[data-lightbox-prev]');
        var lbNext = lightbox.querySelector('[data-lightbox-next]');
        var lbOpener = null;

        var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]),' +
          ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        function isOpen() {
          return lightbox.classList.contains('is-open');
        }

        function focusable() {
          return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (element) {
            return element.offsetWidth > 0 || element.offsetHeight > 0;
          });
        }

        function lbIndex() {
          var width = rail.clientWidth || 1;
          return Math.min(Math.round(rail.scrollLeft / width), lbSlides.length - 1);
        }

        function lbGo(index, instant) {
          if (!lbSlides[index] || !lbSlides[0]) return;
          rail.scrollTo({
            left: lbSlides[index].offsetLeft - lbSlides[0].offsetLeft,
            behavior: instant || REDUCED ? 'auto' : 'smooth'
          });
        }

        function updateLbNav() {
          var index = lbIndex();
          if (lbPrev) lbPrev.disabled = index <= 0;
          if (lbNext) lbNext.disabled = index >= lbSlides.length - 1;
        }

        function open(mediaId, trigger) {
          if (isOpen()) return;
          lbOpener = trigger || null;
          lightbox.classList.add('is-open');
          document.documentElement.style.overflow = 'hidden';
          var start = 0;
          for (var i = 0; i < lbSlides.length; i++) {
            if (lbSlides[i].getAttribute('data-media-id') === String(mediaId)) {
              start = i;
              break;
            }
          }
          lbGo(start, true);
          updateLbNav();
          panel.focus();
        }

        function close() {
          if (!isOpen()) return;
          lightbox.classList.remove('is-open');
          document.documentElement.style.overflow = '';
          if (lbOpener && document.contains(lbOpener)) lbOpener.focus();
          lbOpener = null;
        }

        if (lbPrev) {
          lbPrev.addEventListener('click', function () {
            lbGo(Math.max(0, lbIndex() - 1));
          });
        }
        if (lbNext) {
          lbNext.addEventListener('click', function () {
            lbGo(Math.min(lbSlides.length - 1, lbIndex() + 1));
          });
        }
        rail.addEventListener('scroll', updateLbNav, { passive: true });

        lightbox.addEventListener('click', function (event) {
          var target = event.target;
          if (!target || !target.closest) return;
          if (target.closest('[data-lightbox-close]') || target.closest('[data-lightbox-overlay]')) {
            close();
            return;
          }
          /* Een klik in de lege ruimte van een slide — buiten het beeld en
             buiten de knoppen — sluit ook. */
          if (target.closest('[data-lightbox-slide]') && !target.closest('img')) close();
        });

        panel.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
          }
          if (event.key === 'ArrowLeft') {
            lbGo(Math.max(0, lbIndex() - 1));
            return;
          }
          if (event.key === 'ArrowRight') {
            lbGo(Math.min(lbSlides.length - 1, lbIndex() + 1));
            return;
          }
          if (event.key !== 'Tab') return;

          var items = focusable();
          if (!items.length) {
            event.preventDefault();
            panel.focus();
            return;
          }

          var first = items[0];
          var last = items[items.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });

        return { open: open, close: close, isOpen: isOpen };
      })();

      slides.forEach(function (slide) {
        var zoom = slide.querySelector('[data-gallery-zoom]');
        if (!zoom) return;
        zoom.addEventListener('click', function () {
          if (lightboxApi) lightboxApi.open(slide.getAttribute('data-media-id'), zoom);
        });
      });
    }

    function showMedia(id) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(id)) {
          goToSlide(i);
          return;
        }
      }
    }

    /* -- Aantal -- */
    root.querySelectorAll('[data-quantity]').forEach(function (box) {
      var input = box.querySelector('input');
      if (!input) return;
      box.querySelectorAll('[data-quantity-step]').forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(button.getAttribute('data-quantity-step'), 10) || 0;
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          var next = (parseInt(input.value, 10) || min) + step;
          input.value = String(Math.max(min, next));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });

    /* -- Varianten -- */
    var picker = root.querySelector('[data-variant-picker]');
    var idInputs = root.querySelectorAll('[data-variant-id]');
    var priceEl = root.querySelector('[data-price]');
    var compareEl = root.querySelector('[data-compare-price]');
    var saveEl = root.querySelector('.product-main__save');
    var addButtons = root.querySelectorAll('[data-add-button]');
    var stockEl = root.querySelector('[data-stock]');
    var stockText = root.querySelector('[data-stock-text]');

    /* De variant-JSON levert opgemaakte bedragen ("€ 29,95"). Voor het
       kortingspercentage halen we daar het getal uit: het laatste leesteken
       met maximaal twee cijfers erachter is het decimaalteken, de rest zijn
       duizendtallen. Lukt het niet, dan geeft dit NaN en blijft de badge weg. */
    function parseMoney(text) {
      if (!text) return NaN;
      var digits = String(text).replace(/[^0-9.,]/g, '');
      if (!digits) return NaN;
      var separator = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
      if (separator > -1 && digits.length - separator - 1 <= 2) {
        digits = digits.slice(0, separator).replace(/[.,]/g, '') + '.' + digits.slice(separator + 1);
      } else {
        digits = digits.replace(/[.,]/g, '');
      }
      return parseFloat(digits);
    }

    /* De sale-pil naast de prijs. De waarde komt kant-en-klaar uit de
       variant-JSON: save_percent als getal, save_amount als opgemaakt bedrag.
       Zelf een bedrag samenstellen zou betekenen dat het valutateken en de
       scheidingstekens hier nagebouwd worden, en dat gaat mis zodra de winkel
       in een andere valuta verkoopt.

       De zichtbare tekst wordt opgebouwd uit data-save-template, waarin de
       sectie de vertaalde regel met [value] als plaatshouder heeft gezet. Het
       oude gedrag verving het eerste getal in de bestaande tekst; bij een
       bedrag als "Save € 2,50" zijn dat er meerdere en zou dat de verkeerde
       raken. Ontbreekt de sjabloon (een oudere sectie), dan valt hij terug op
       diezelfde vervanging. */
    function updateSaveBadge(variant) {
      if (!saveEl) return;

      var mode = saveEl.getAttribute('data-save-mode') || 'percent';
      var value = null;

      if (variant && variant.compare_at_price) {
        if (mode === 'amount') {
          value = variant.save_amount || null;
        } else if (variant.save_percent) {
          value = String(variant.save_percent);
        } else {
          /* Terugval voor varianten zonder de nieuwe velden: percentage uit de
             twee bedragen. */
          var price = parseMoney(variant.price);
          var compare = parseMoney(variant.compare_at_price);
          if (price > 0 && compare > price) {
            value = String(Math.round(((compare - price) / compare) * 100));
          }
        }
      }

      if (!value) {
        saveEl.hidden = true;
        return;
      }

      var template = saveEl.getAttribute('data-save-template');
      if (template && template.indexOf('[value]') > -1) {
        saveEl.textContent = template.replace('[value]', value);
      } else {
        saveEl.textContent = saveEl.textContent.replace(/\d+([.,]\d+)?/, value);
      }
      saveEl.hidden = false;
    }

    function selectedValues() {
      var values = [];
      picker.querySelectorAll('fieldset').forEach(function (set) {
        var checked = set.querySelector('input:checked');
        values.push(checked ? checked.value : null);
      });
      return values;
    }

    function matchVariant(values, preferAvailable) {
      var fallback = null;
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var ok = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] !== null && variant.options[j] !== values[j]) { ok = false; break; }
        }
        if (!ok) continue;
        if (!preferAvailable || variant.available) return variant;
        if (!fallback) fallback = variant;
      }
      return fallback;
    }

    function refreshAvailability(values) {
      picker.querySelectorAll('fieldset').forEach(function (set, position) {
        set.querySelectorAll('input').forEach(function (input) {
          var probe = values.slice();
          probe[position] = input.value;
          var match = matchVariant(probe, true);
          var label = set.querySelector('label[for="' + input.id + '"]');
          if (label) label.classList.toggle('is-unavailable', !(match && match.available));
        });
      });
    }

    function update() {
      var values = selectedValues();
      var variant = matchVariant(values, false);

      picker.querySelectorAll('fieldset').forEach(function (set, position) {
        var output = set.querySelector('[data-option-value]');
        if (output && values[position]) output.textContent = values[position];
      });

      refreshAvailability(values);

      /* Geen geldige combinatie: knop op "Unavailable" en de prijs- en
         voorraadinformatie van de vorige variant weg, anders staat er
         tegenstrijdige informatie naast elkaar. */
      if (!variant) {
        addButtons.forEach(function (button) {
          button.disabled = true;
          button.textContent = button.getAttribute('data-label-unavailable');
        });
        if (priceEl) priceEl.textContent = '';
        if (compareEl) compareEl.hidden = true;
        updateSaveBadge(null);
        if (stockEl) stockEl.hidden = true;
        return;
      }

      idInputs.forEach(function (input) { input.value = variant.id; });
      /* variant.price komt als opgemaakt bedrag uit de sectie
         (`| money | strip_html | json`), dus textContent volstaat. */
      if (priceEl) priceEl.textContent = variant.price;
      if (compareEl) {
        if (variant.compare_at_price) {
          compareEl.hidden = false;
          var strike = compareEl.querySelector('s');
          if (strike) strike.textContent = variant.compare_at_price;
        } else {
          compareEl.hidden = true;
        }
      }
      updateSaveBadge(variant);

      addButtons.forEach(function (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available
          ? button.getAttribute('data-label-add')
          : button.getAttribute('data-label-sold-out');
      });

      if (stockEl && stockText) {
        stockEl.hidden = false;
        stockEl.classList.toggle('is-out', !variant.available);
        stockText.textContent = variant.available
          ? stockEl.getAttribute('data-text-in')
          : stockEl.getAttribute('data-text-out');
      }

      if (variant.media_id) showMedia(variant.media_id);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    if (picker && variants.length) {
      picker.addEventListener('change', update);
      refreshAvailability(selectedValues());
    }

    /* -- Balk die bij scrollen verschijnt -- */
    var sticky = root.querySelector('[data-sticky-bar]');
    var anchor = root.querySelector('[data-sticky-anchor]');
    var observer = null;
    /* De balk hangt onder de header. Die header loopt mee met scrollen en
       kan van hoogte veranderen (aankondiging weggeklikt, ander scherm), dus
       de positie wordt gemeten in plaats van vastgezet. */
    if (sticky) {
      var headerSection = document.querySelector('.shopify-section--header');
      var stickyTopFrame = null;
      var setStickyTop = function () {
        stickyTopFrame = null;
        var bottom = headerSection ? Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom)) : 0;
        document.documentElement.style.setProperty('--sticky-bar-top', bottom + 'px');
      };
      var queueStickyTop = function () {
        if (stickyTopFrame === null) stickyTopFrame = requestAnimationFrame(setStickyTop);
      };
      setStickyTop();
      window.addEventListener('scroll', queueStickyTop, { passive: true });
      window.addEventListener('resize', queueStickyTop);
      if (headerSection && 'ResizeObserver' in window) {
        new ResizeObserver(queueStickyTop).observe(headerSection);
      }
    }

    if (sticky && anchor && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', passed);
      }, { threshold: 0 });
      observer.observe(anchor);
    }

    /* Het verborgen aantalveld in de sticky balk volgt het hoofd-aantalveld,
       zodat de balk hetzelfde aantal toevoegt als het formulier erboven. */
    var stickyQuantity = root.querySelector('[data-sticky-quantity]');
    var quantityInput = root.querySelector('[data-quantity-input]');
    if (stickyQuantity && quantityInput) {
      var syncStickyQuantity = function () {
        stickyQuantity.value = quantityInput.value;
      };
      quantityInput.addEventListener('change', syncStickyQuantity);
      quantityInput.addEventListener('input', syncStickyQuantity);
      syncStickyQuantity();
    }

    return {
      destroy: function () {
        if (observer) observer.disconnect();
        /* Een open lightbox houdt het scrollslot op <html> vast; bij het
           herladen van de sectie in de themabewerker geven we dat terug. */
        if (lightboxApi && lightboxApi.isOpen()) lightboxApi.close();
      }
    };
  }

  /* ---- Winkelwagenpaneel ---------------------------------------------- */

  /* De la zelf staat in `sections/cart-drawer.liquid`. Wijzigen en verwijderen
     lopen via de Cart AJAX API met gebundelde section rendering: het antwoord
     bevat de verse HTML van de sectie en daarvan vervangen we alleen
     `[data-cart-drawer-inner]`. De schil eromheen — paneel, overlay en de
     afhandelaars hieronder — blijft staan, dus er hoeft niets opnieuw
     gekoppeld te worden. Alle knoppen in de inhoud lopen daarom via
     gedelegeerde klikken, niet via losse listeners per regel.

     `cartDrawer` is de enige verwijzing naar de huidige instantie. Is die
     null, dan bestaat het paneel niet en gedraagt het thema zich als een
     thema zonder JavaScript: het winkelwagenicoon is een link, en een
     toevoegformulier verzendt gewoon naar /cart/add. */

  var cartDrawer = null;

  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function CartDrawer(root) {
    /* Op de winkelwagenpagina is de pagina de winkelwagen. Een paneel dat de
       tabel eronder niet bijwerkt zou daar uit de pas gaan lopen, dus laten we
       het daar dicht en blijft het icoon een gewone link. */
    if (document.body.classList.contains('template-cart')) return;

    var panel = root.querySelector('[data-cart-drawer-panel]');
    var inner = root.querySelector('[data-cart-drawer-inner]');
    if (!panel || !inner) return;

    var sectionId = root.getAttribute('data-section-id');
    var recommendationCache = {};
    var opener = null;
    var busy = false;

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]),' +
      ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function isOpen() {
      return root.classList.contains('is-open');
    }

    function focusable() {
      return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (element) {
        return element.offsetWidth > 0 || element.offsetHeight > 0;
      });
    }

    function markTriggers(state) {
      document.querySelectorAll('[data-cart-drawer-open]').forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', String(state));
      });
    }

    /* Focus zetten zonder de browser naar het element te laten scrollen.
       Bij het openen staat het paneel nog rechts buiten beeld
       (transform: translateX(100%)) en is `.cart-drawer` een scrollcontainer:
       `overflow: hidden` maakt hem scrollbaar voor de browser, alleen niet voor
       de gebruiker. Een gewone focus() laat de browser het paneel dan in beeld
       schuiven terwijl de transform hem tegelijk terugtrekt — het paneel schiet
       eerst weg en komt daarna terug. scrollLeft gaat er achteraan terug op nul
       voor browsers die preventScroll negeren. Zelfde hulpje als in de mobiele
       navigatie, om dezelfde reden. */
    function focusIn(element) {
      if (!element) return;
      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
      root.scrollLeft = 0;
    }

    function open(trigger) {
      if (isOpen()) return;
      opener = trigger || null;
      root.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      markTriggers(true);
      focusIn(focusable()[0] || panel);
      loadRecommendations();
    }

    function close() {
      if (!isOpen()) return;
      root.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      markTriggers(false);
      if (opener && document.contains(opener)) opener.focus();
      opener = null;
    }

    /* Het aantal in de kop staat er altijd, ook op nul; hier zetten we het bij
       zonder de hele kopsectie opnieuw op te halen. */
    function setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (element) {
        element.textContent = String(count);
        element.hidden = count === 0;
      });
    }

    /* Na een wijziging wordt de inhoud vervangen en is het element waar de
       focus op stond weg. Zonder herstel valt de focus terug op <body> en is
       de la met het toetsenbord niet meer te bedienen. We onthouden daarom
       welke knop het was en zoeken hem terug; lukt dat niet, dan gaat de focus
       naar het paneel zelf, zodat hij binnen de dialoog blijft. */
    function rememberFocus() {
      var active = document.activeElement;
      if (!active || !panel.contains(active) || !active.getAttribute) return null;
      var line = active.getAttribute('data-line');
      if (!line) return null;
      if (active.hasAttribute('data-cart-step')) {
        return '[data-cart-step][data-line="' + line + '"][data-step="' +
          active.getAttribute('data-step') + '"]';
      }
      if (active.hasAttribute('data-cart-quantity')) {
        return '[data-cart-quantity][data-line="' + line + '"]';
      }
      if (active.hasAttribute('data-cart-remove')) {
        return '[data-cart-remove][data-line="' + line + '"]';
      }
      return null;
    }

    function restoreFocus(selector) {
      if (!isOpen()) return;
      var target = selector ? inner.querySelector(selector) : null;
      focusIn(target || panel);
    }

    /* Shopify stuurt bij een fout vaak een leesbare uitleg mee (bijvoorbeeld
       "You can only add 2 of this item"); die tonen we, en alleen zonder
       uitleg valt het bericht terug op de generieke tekst uit de sectie. De
       generieke tekst onthouden we, want na één specifieke melding zou hij
       anders blijven hangen. */
    function showError(message) {
      var box = inner.querySelector('[data-cart-error]');
      if (!box) return;
      if (!box.dataset.genericText) box.dataset.genericText = box.textContent;
      box.textContent = (message && typeof message === 'string')
        ? message
        : box.dataset.genericText;
      box.hidden = false;
    }

    /* Na een geslaagde wijziging krijgt de blijvende statusregio buiten de
       vervangen inhoud een korte melding — de al vertaalde aantalregel uit de
       verse kop, zodat er geen tekst in dit bestand staat. */
    function announceStatus() {
      var status = panel.querySelector('[data-cart-status]');
      if (!status) return;
      var count = inner.querySelector('.cart-drawer__count');
      status.textContent = count ? count.textContent.trim() : '';
    }

    function render(html) {
      if (!html) {
        showError();
        return;
      }
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('[data-cart-drawer-inner]');
      if (!fresh) {
        showError();
        return;
      }
      inner.innerHTML = fresh.innerHTML;
      var count = parseInt(fresh.getAttribute('data-item-count'), 10);
      if (!isNaN(count)) setCount(count);
      announceStatus();
      loadRecommendations();
    }

    function request(url, options) {
      busy = true;
      root.classList.add('is-busy');
      return fetch(url, options)
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (data.status && data.status >= 400) showError(data.description || data.message);
          else if (data.sections) render(data.sections[sectionId]);
          else showError();
        })
        .catch(function () { showError(); })
        .then(function () {
          busy = false;
          root.classList.remove('is-busy');
        });
    }

    /* Aantal wijzigen en verwijderen zijn dezelfde handeling: verwijderen is
       `quantity: 0`. `line` is de positie in de wagen, 1-gebaseerd. `trigger`
       is de aangeklikte knop; die krijgt de laadstate zolang het verzoek
       loopt. Slaagt het verzoek, dan verdwijnt de knop toch met de vervangen
       inhoud. */
    function change(line, quantity, trigger) {
      if (busy || isNaN(line)) return;
      if (trigger) trigger.classList.add('is-loading');
      var key = rememberFocus();
      request(routeRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: sectionId })
      }).then(function () {
        if (trigger) trigger.classList.remove('is-loading');
        restoreFocus(key);
      });
    }

    function add(form) {
      open(document.activeElement);
      if (busy) return;
      /* Laadstate op de verzendknop, en pas weer klikbaar als het verzoek
         klaar is — ook na een fout. Zo verdwijnt een klik tijdens een lopend
         verzoek niet meer geluidloos. */
      var submit = form.querySelector('[type="submit"]') || form.querySelector('button:not([type])');
      if (submit) {
        submit.classList.add('is-loading');
        submit.disabled = true;
      }
      var body = new FormData(form);
      body.append('sections', sectionId);
      request(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      }).then(function () {
        if (submit) {
          submit.classList.remove('is-loading');
          submit.disabled = false;
        }
        /* Bij toevoegen vanaf een productpagina staat de focus buiten de la;
           die halen we het paneel in, anders werkt de focusval niet. */
        restoreFocus(null);
      });
    }

    /* De opmerking bij de bestelling gaat normaal pas bij het afrekenen mee.
       Hier slaan we hem kort na elke wijziging al op via /cart/update.js,
       zodat hij bewaard blijft als de bezoeker de la sluit. Fouten blijven
       stil: de note gaat bij het afrekenen alsnog gewoon mee. */
    var noteTimer = null;

    function saveNote(value) {
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(function () {
        fetch(routeRoot() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value })
        }).catch(function () {});
      }, 300);
    }

    /* Aanbevelingen komen van Shopify zelf. Geen antwoord of een leeg antwoord
       is geen fout: dan blijft het blok verborgen. */
    function loadRecommendations() {
      var host = inner.querySelector('[data-cart-recommendations]');
      if (!host) return;
      var slot = host.querySelector('[data-cart-recommendations-slot]');
      var productId = host.getAttribute('data-product-id');
      if (!slot || !productId) return;

      var url = routeRoot() + 'recommendations/products' +
        '?section_id=' + encodeURIComponent(host.getAttribute('data-rec-section')) +
        '&product_id=' + encodeURIComponent(productId) +
        '&limit=' + encodeURIComponent(host.getAttribute('data-limit')) +
        '&intent=related';

      if (recommendationCache[url]) {
        slot.innerHTML = recommendationCache[url];
        host.hidden = false;
        return;
      }

      fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (text) {
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var list = parsed.querySelector('[data-cart-recommendations-list]');
          if (!list || !list.children.length) return;
          recommendationCache[url] = list.outerHTML;
          slot.innerHTML = recommendationCache[url];
          host.hidden = false;
        })
        .catch(function () {});
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-cart-drawer-close]') || target.closest('[data-cart-drawer-overlay]')) {
        close();
        return;
      }

      var step = target.closest('[data-cart-step]');
      if (step) {
        change(
          parseInt(step.getAttribute('data-line'), 10),
          parseInt(step.getAttribute('data-quantity'), 10),
          step
        );
        return;
      }

      var remove = target.closest('[data-cart-remove]');
      if (remove) {
        change(parseInt(remove.getAttribute('data-line'), 10), 0, remove);
      }
    });

    root.addEventListener('change', function (event) {
      if (event.target && event.target.name === 'note') {
        saveNote(event.target.value);
        return;
      }
      var input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      var value = parseInt(input.value, 10);
      /* Een leeggemaakt of onleesbaar veld is geen verwijderopdracht: zet de
         vorige waarde terug. Verwijderen blijft aan de remove-knop (en een
         expliciete 0) voorbehouden. */
      if (isNaN(value)) {
        input.value = input.defaultValue;
        return;
      }
      if (value < 0) value = 0;
      change(parseInt(input.getAttribute('data-line'), 10), value);
    });

    /* Escape sluit, en de focus blijft binnen het paneel zolang het open is. */
    panel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = focusable();
      if (!items.length) {
        event.preventDefault();
        focusIn(panel);
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        focusIn(last);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        focusIn(first);
      }
    });

    var api = { open: open, close: close, add: add, isOpen: isOpen };
    cartDrawer = api;

    return {
      destroy: function () {
        if (isOpen()) close();
        window.clearTimeout(noteTimer);
        if (cartDrawer === api) cartDrawer = null;
      }
    };
  }

  /* ---- Mobiele navigatie ---------------------------------------------- */
  /* Het hamburgericoon is een link naar `#MobileNav`, net zoals het zoekicoon
     een link naar de zoekpagina is en het wagentje een link naar /cart. Zonder
     dit script vouwt `:target` de navigatie open als een blok in de pagina en
     doen de `<details>` de subniveaus. Hieronder wordt het een schermvullend
     paneel: van links inschuiven, Escape terug of dicht, de focus gaat het
     paneel in, blijft erin en keert terug naar het icoon dat het opende. */

  function MobileNav(root) {
    var scope = root.closest('[data-header]') || document;
    var openers = scope.querySelectorAll('[data-mobile-nav-open]');
    if (!openers.length) return;

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]),' +
      ' select:not([disabled]), textarea:not([disabled]), summary,' +
      ' [tabindex]:not([tabindex="-1"])';

    var opener = null;
    var activeSub = null;

    /* Vanaf hier is het een dialoog en geen blok in de pagina meer. Het label
       komt uit de vertaling mee als data-attribuut; er staat geen Engels in
       dit bestand. */
    root.classList.add('mobile-nav--js');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('tabindex', '-1');
    root.setAttribute('aria-hidden', 'true');
    var label = root.getAttribute('data-label');
    if (label) root.setAttribute('aria-label', label);

    function isOpen() {
      return root.classList.contains('is-open');
    }

    /* Een subpaneel staat nog rechts buiten beeld op het moment dat de focus
       erheen gaat. De browser sleept het dan in beeld door de kop zelf opzij te
       scrollen — die is `overflow: hidden`, maar blijft een scrollcontainer.
       Alle focus in dit paneel loopt daarom via deze ene plek. */
    function focusIn(element) {
      if (!element) return;
      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
      root.scrollLeft = 0;
    }

    /* Wat achter het subpaneel ligt staat in CSS op `visibility: hidden` en is
       dus al niet meer aanklikbaar; hier houden we dezelfde grens aan, zodat
       de eerste en de laatste van de tabronde kloppen. */
    function focusable() {
      return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (element) {
        var sub = element.closest('[data-mobile-nav-sub]');
        if (sub) {
          if (!activeSub || sub !== activeSub.panel) return false;
        } else if (activeSub && element.closest('[data-mobile-nav-scroll]')) {
          return false;
        }
        return element.offsetWidth > 0 || element.offsetHeight > 0;
      });
    }

    function markOpeners(state) {
      openers.forEach(function (element) {
        element.setAttribute('aria-expanded', String(state));
      });
    }

    /* Het zoekpaneel klapt onder de kopbalk open en zou achter dit paneel
       blijven staan. De kop leest zijn toestand uit de DOM, dus dit blijft
       kloppen zonder dat de twee elkaar hoeven te kennen. */
    function closeSearch() {
      var panel = document.querySelector('[data-search-panel]');
      if (!panel || panel.hidden) return;
      panel.hidden = true;
      document.querySelectorAll('[data-search-toggle]').forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      });
      // De la heeft ook een donkere laag en een klasse op het document; die
      // horen hier mee weg, anders blijft het scherm grijs na Escape.
      var overlay = document.querySelector('[data-search-overlay]');
      if (overlay) overlay.hidden = true;
      document.documentElement.classList.remove('is-search-open');
      var results = document.querySelector('[data-search-results]');
      if (results) results.innerHTML = '';
    }

    function closeSub(focusSummary) {
      if (!activeSub) return;
      var summary = activeSub.summary;
      activeSub.panel.classList.remove('is-active');
      summary.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-sub-open');
      activeSub = null;
      if (focusSummary) focusIn(summary);
    }

    function openSub(entry) {
      if (activeSub === entry) return;
      closeSub(false);
      entry.panel.classList.add('is-active');
      entry.summary.setAttribute('aria-expanded', 'true');
      root.classList.add('is-sub-open');
      activeSub = entry;
      focusIn(entry.panel.querySelector('[data-mobile-nav-back]'));
    }

    function open(trigger) {
      if (isOpen()) return;
      opener = trigger || null;
      closeSearch();
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      markOpeners(true);
      focusIn(focusable()[0] || root);
    }

    function close() {
      if (!isOpen()) return;
      closeSub(false);
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      markOpeners(false);
      if (opener && document.contains(opener)) focusIn(opener);
      opener = null;
    }

    openers.forEach(function (element) {
      element.setAttribute('aria-expanded', 'false');
      element.addEventListener('click', function (event) {
        event.preventDefault();
        if (isOpen()) close();
        else open(element);
      });
    });

    root.querySelectorAll('[data-mobile-nav-close]').forEach(function (button) {
      button.addEventListener('click', function () { close(); });
    });

    /* De subniveaus. `<details>` blijft hier bewust openstaan: een dichte
       `<details>` verbergt zijn inhoud, en dan valt er niets te schuiven. De
       klik op de samenvatting wordt onderschept, dus hij klapt niet echt uit —
       het paneel schuift eroverheen en de terugknop brengt je weer terug. */
    root.querySelectorAll('[data-mobile-nav-details]').forEach(function (details) {
      var summary = details.querySelector('summary');
      var panel = details.querySelector('[data-mobile-nav-sub]');
      if (!summary || !panel) return;

      details.open = true;
      summary.setAttribute('aria-expanded', 'false');

      var entry = { summary: summary, panel: panel };

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        openSub(entry);
      });

      panel.querySelectorAll('[data-mobile-nav-back]').forEach(function (back) {
        back.addEventListener('click', function () { closeSub(true); });
      });
    });

    /* Escape gaat eerst een niveau terug en sluit pas daarna het paneel. */
    function onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !isOpen()) return;
      event.preventDefault();
      if (activeSub) closeSub(true);
      else close();
    }
    document.addEventListener('keydown', onDocumentKeydown);

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || !isOpen()) return;

      var items = focusable();
      if (!items.length) {
        event.preventDefault();
        focusIn(root);
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) {
        event.preventDefault();
        focusIn(last);
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        focusIn(first);
      }
    });

    /* Boven 1024px verdwijnt het paneel uit de opmaak. Blijft het dan "open"
       staan, dan houdt de pagina zijn scrollslot. */
    var wide = window.matchMedia('(min-width: 1024px)');
    var onWide = function (event) {
      if (event.matches) close();
    };
    if (typeof wide.addEventListener === 'function') wide.addEventListener('change', onWide);
    else if (typeof wide.addListener === 'function') wide.addListener(onWide);

    return {
      destroy: function () {
        if (isOpen()) close();
        document.removeEventListener('keydown', onDocumentKeydown);
        if (typeof wide.removeEventListener === 'function') wide.removeEventListener('change', onWide);
        else if (typeof wide.removeListener === 'function') wide.removeListener(onWide);
      }
    };
  }

  /* ---- Films die pas laden als ze in beeld komen ----------------------- */

  /* De films van de How it's made-sectie zijn samen ruim 30 MB, dus geen enkele
     `<video>` heeft in de HTML een `src`. De bron staat in `data-src` en komt
     er hier pas in wanneer het vlak in beeld scrollt; scrollt het weer weg, dan
     pauzeert de film. Zo draait er nooit meer dan één tegelijk en haalt een
     bezoeker die de sectie overslaat geen enkele film binnen.

     Bij `prefers-reduced-motion` start er niets vanzelf: het posterframe blijft
     staan en de knop in het vlak verschijnt, zodat de bezoeker zelf begint.
     Diezelfde knop komt tevoorschijn wanneer de browser het automatisch
     afspelen weigert — dan is er altijd nog een weg naar de film.

     Zonder JavaScript gebeurt hier niets en blijft het `<img>` met het
     posterframe staan dat de sectie zelf al rendert. */

  function LazyVideo(root) {
    var video = root.querySelector('video');
    if (!video) return;

    var source = video.getAttribute('data-src');
    if (!source) return;

    var toggle = root.querySelector('[data-lazy-video-toggle]');
    var playLabel = root.getAttribute('data-play-label') || 'Play';
    var pauseLabel = root.getAttribute('data-pause-label') || 'Pause';
    var observer = null;

    /* Het attribuut `muted` staat al in de HTML, maar sommige browsers kijken
       naar de property voordat ze automatisch afspelen toestaan. */
    video.muted = true;

    function load() {
      if (video.getAttribute('src')) return;
      video.setAttribute('src', source);
      root.classList.add('is-active');
    }

    function revealToggle() {
      if (toggle) toggle.hidden = false;
    }

    function setState(playing) {
      root.classList.toggle('is-playing', playing);
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    }

    function play() {
      load();
      var attempt = video.play();
      /* Oudere browsers geven niets terug; nieuwe een promise die afketst als
         automatisch afspelen niet mag. Dan is de knop het antwoord. */
      if (attempt && typeof attempt.catch === 'function') attempt.catch(revealToggle);
    }

    video.addEventListener('play', function () { setState(true); });
    video.addEventListener('pause', function () { setState(false); });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused) play();
        else video.pause();
      });
    }

    /* Geen waarnemer bij bewegingsbeperking, en ook niet als de browser er geen
       heeft: in beide gevallen blijft het poster staan en doet de knop het werk. */
    if (REDUCED || !('IntersectionObserver' in window)) {
      revealToggle();
      return {
        destroy: function () { video.pause(); }
      };
    }

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) play();
        else if (!video.paused) video.pause();
      });
    }, { threshold: 0.35 });
    observer.observe(root);

    return {
      destroy: function () {
        if (observer) observer.disconnect();
        video.pause();
      }
    };
  }

  /* --- Maattabel ---------------------------------------------------------
     De link is een gewone link naar de pagina; hier wordt hij een schakelaar
     voor het venster. Werkt het script niet, dan opent de pagina zelf, en dat
     is een prima uitkomst.

     Escape sluit, klikken op de donkere laag ook, en de focus gaat terug naar
     de link zodat je met het toetsenbord niet kwijtraakt waar je was. */
  document.querySelectorAll('[data-size-guide-open]').forEach(function (link) {
    var panel = document.getElementById(link.getAttribute('aria-controls'));
    if (!panel) return;

    function setGuide(state) {
      panel.hidden = !state;
      link.setAttribute('aria-expanded', String(state));
      document.documentElement.classList.toggle('is-guide-open', state);
      if (!state) link.focus();
    }

    link.addEventListener('click', function (event) {
      event.preventDefault();
      setGuide(true);
      var close = panel.querySelector('[data-size-guide-close]');
      if (close && close.focus) close.focus();
    });

    panel.querySelectorAll('[data-size-guide-close]').forEach(function (b) {
      b.addEventListener('click', function () { setGuide(false); });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !panel.hidden) setGuide(false);
    });
  });

  /* --- Automatische productaanbevelingen ---------------------------------
     Shopify bepaalt deze zelf op basis van wat er in deze winkel samen wordt
     gekocht en bekeken (intent=related). Er is dus geen invulwerk per product
     voor nodig, in tegenstelling tot de keuzes uit de Search & Discovery-app.

     Het blok staat verborgen in de pagina en wordt pas getoond als er echt
     producten terugkomen; geen antwoord of een leeg antwoord is geen fout,
     dan blijft het blok gewoon weg in plaats van een lege kop achter te
     laten. Zelfde patroon als de aanbevelingen in het winkelwagenpaneel. */
  function initProductRecommendations(scope) {
  (scope || document).querySelectorAll('[data-product-recommendations]').forEach(function (host) {
    if (host.dataset.recLoaded === 'true') return;
    host.dataset.recLoaded = 'true';
    var slot = host.querySelector('[data-recommendations-slot]');
    var productId = host.getAttribute('data-product-id');
    if (!slot || !productId) return;

    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    var url = (root || '/') + 'recommendations/products' +
      '?section_id=' + encodeURIComponent(host.getAttribute('data-rec-section')) +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=' + encodeURIComponent(host.getAttribute('data-limit') || 4) +
      '&intent=related';

    fetch(url)
      .then(function (response) { return response.text(); })
      .then(function (text) {
        var parsed = new DOMParser().parseFromString(text, 'text/html');
        var list = parsed.querySelector('[data-recommendations-list]');
        if (!list || !list.children.length) return;
        // Vervangen en niet aanvullen: in de slot staat de terugval uit de
        // sectie (producten uit dezelfde collectie). Zodra Shopify echte
        // aanbevelingen teruggeeft, gaan die ervoor.
        slot.innerHTML = '';
        slot.appendChild(list);
        host.hidden = false;
      })
      .catch(function () {});
  });
  }

  initProductRecommendations();

  /* De themabewerker tekent een sectie opnieuw zodra je aan een instelling
     draait. Het nieuwe stuk pagina heeft dan nog geen aanbevelingen opgehaald,
     dus zonder deze regel val je daar terug op de collectielijst. Op de echte
     winkel gebeurt dat hertekenen niet; dit is puur voor de editor. */
  document.addEventListener('shopify:section:load', function (event) {
    initProductRecommendations(event.target);
  });

  /* ---- Opstarten ------------------------------------------------------ */

  var INITIALISERS = [
    ['[data-announcement]', Announcement],
    ['[data-carousel]', Carousel],
    ['[data-filters]', Filters],
    ['[data-header]', Header],
    ['[data-mobile-nav]', MobileNav],
    ['[data-collection-filters]', CollectionFilters],
    ['[data-read-more]', ReadMore],
    ['[data-product-main]', ProductMain],
    ['[data-cart-drawer]', CartDrawer],
    ['[data-lazy-video]', LazyVideo]
  ];

  /* Elke aangemaakte instantie wordt hier bijgehouden, met zijn wortelelement.
     Een component die listeners buiten zijn eigen wortel zet (window,
     document, matchMedia) geeft een object met `destroy()` terug; bij
     `shopify:section:unload` ruimen we die op, anders stapelen de listeners
     zich in de themabewerker bij elke herlaadbeurt op. */
  var instances = [];

  function init(scope) {
    INITIALISERS.forEach(function (pair) {
      scope.querySelectorAll(pair[0]).forEach(function (element) {
        if (element.dataset.themeReady === 'true') return;
        element.dataset.themeReady = 'true';
        var instance = pair[1](element);
        instances.push({ root: element, instance: instance });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* De themabewerker vervangt secties zonder de pagina te herladen. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (entry) {
      if (!event.target.contains(entry.root)) return true;
      if (entry.instance && typeof entry.instance.destroy === 'function') {
        entry.instance.destroy();
      }
      return false;
    });
  });

  /* Iets in de wagen leggen: bestaat het paneel, dan onderscheppen we het
     formulier en schuift de la open. Eén afhandelaar op document, zodat
     productkaarten die later in beeld komen — en de aanbevelingen in het
     paneel zelf — er vanzelf onder vallen. Zonder paneel of zonder
     JavaScript verzendt hetzelfde formulier gewoon naar /cart/add. */
  document.addEventListener('submit', function (event) {
    if (!cartDrawer) return;
    var form = event.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    event.preventDefault();
    cartDrawer.add(form);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (cartDrawer && cartDrawer.isOpen()) cartDrawer.close();
  });
})();

/* --- Video in de productgalerij ------------------------------------------
   Video's spelen vanzelf af, zonder geluid en in een lus, zodra hun slide
   voor minstens de helft in beeld is. Pas dan laadt de browser het bestand.
   Uit beeld (een andere slide staat voor, of de galerij is weggescrold)
   pauzeert de video weer. Bij de
   systeeminstelling "minder beweging" speelt er niets vanzelf; dan krijgt
   de video bedieningsknoppen en start je hem zelf. */
(function () {
  'use strict';
  var videos = document.querySelectorAll('[data-gallery-video]');
  if (!videos.length) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  videos.forEach(function (video) {
    video.muted = true;
    if (reduce) video.controls = true;
  });

  if (reduce || !('IntersectionObserver' in window)) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.intersectionRatio >= 0.5) {
        var attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      } else {
        video.pause();
      }
    });
  }, { threshold: [0, 0.5] });

  videos.forEach(function (video) { observer.observe(video); });
})();
